import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Spinner, Form, InputGroup, Alert, Nav } from 'react-bootstrap';
import axios from 'axios';
import CustomEditor from './Editor';
import { useSelector } from "react-redux";
import { CirclePicker, ChromePicker } from "react-color";
import { BsEyedropper, BsTrash3, BsPlus, BsCheckAll, BsDashCircle } from "react-icons/bs";
import Select from 'react-select';
import styles from './ItemModal.module.css';
import { baseURL } from "../helpers/baseURL";
import { headers } from '../helpers/Requests';
import { showDeleteConfirm } from './ItemDelete';
import FormRule from './FormRule';
import { colorOption, colorValue, colorValueSingle, imageOption } from './SelectComponents';
import { ucFirst } from '../helpers/generic';
import {
	INCOMING_EMAIL_CONFIRMATION_TEMPLATES,
	getDefaultIncomingEmailConfirmationHtml,
} from '../helpers/incomingEmailConfirmationTemplates';

const timezoneOptions = Intl.supportedValuesOf('timeZone').map(tz => {
	const offset = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
		.formatToParts()
		.find(p => p.type === 'timeZoneName')?.value ?? '';
	const offsetNum = offset === 'GMT' ? 0 : parseFloat(offset.replace('GMT', '').replace(':', '.'));
	return { value: tz, label: `(${offset}) ${tz}`, offset: offsetNum };
}).sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value));

function ItemModal({ closeModal, currentState, itemType, single, reduxRefresh, prefixAPI }) {
	const [isLoading, setIsLoading] = useState(false);
	const [colorPicker, setColorPicker] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [notifEmailInput, setNotifEmailInput] = useState('');
	const [activeTab, setActiveTab] = useState('general');
	const [emailTemplateIndex, setEmailTemplateIndex] = useState(0);
	const colorPickerRef = useRef(null);
	const notificationEditorRef = useRef(null);

	// form data
	const [formData, setFormData] = useState({
		name: '',
		color: '#C1C1C1',
		description: '',
		members: [],
		departments: [],
		department: null,
		role: '',
		title: '',
		workspace_active: true,
		email: '',
		email_prefix: '',
		notification_emails: [],
		notification_statuses: [],
		notification_type: 'immediately',
		notification_days: [],
		notification_time: '',
		timezone: '',
		auto_assign_user_ids: [],
		submitter_notification_body: '',
		allow_ticket_tracking: true,
		send_submitter_confirmation: true,
	});

	useEffect(() => {
		if (currentState?.title === 'Edit' && currentState?.item) {
			const { name, color, email, email_prefix, title, description, members, departments, department, role, workspace_active, notification_emails, notification_statuses, notification_type, notification_days, notification_time, timezone, auto_assign_user_ids, submitter_notification_body, allow_ticket_tracking, send_submitter_confirmation } = currentState.item;
			const memberOptions = members?.map(member => ({ value: member.id, label: member.name, description: member.email, image: member.profile_image })) ?? [];
			setFormData({
				name: name ?? '',
				color: color ?? '#C1C1C1',
				email: email ?? '',
				email_prefix: email_prefix ?? '',
				title: title ?? '',
				description: description ?? '',
				members: memberOptions,
				departments: departments?.map(department => ({ value: department.id, label: department.name, color: department.color })) ?? [],
				department: department ? { value: department.id, label: department.name, color: department.color } : null,
				role: role ?? '',
				workspace_active: workspace_active ?? true,
				notification_emails: (notification_emails ?? []).map(e => ({ value: e, label: e })),
				notification_statuses: notification_statuses ?? [],
				notification_type: notification_type ?? 'immediately',
				notification_days: notification_days ?? [],
				notification_time: notification_time ?? '',
				timezone: timezone ?? '',
				auto_assign_user_ids: (auto_assign_user_ids ?? []).map(id => Number(id)).filter(Boolean),
				submitter_notification_body: (submitter_notification_body && String(submitter_notification_body).trim())
					? submitter_notification_body
					: getDefaultIncomingEmailConfirmationHtml(),
				allow_ticket_tracking: !!allow_ticket_tracking,
				send_submitter_confirmation: send_submitter_confirmation !== false,
			});
			setEmailTemplateIndex(0);
			setActiveTab('general');
		} else if (currentState?.title === 'Add' && itemType === 'emails') {
			setFormData({
				name: '',
				color: '#C1C1C1',
				description: '',
				members: [],
				departments: [],
				department: null,
				role: '',
				title: '',
				workspace_active: true,
				email: '',
				email_prefix: '',
				notification_emails: [],
				notification_statuses: [],
				notification_type: 'immediately',
				notification_days: [],
				notification_time: '',
				timezone: '',
				auto_assign_user_ids: [],
				submitter_notification_body: getDefaultIncomingEmailConfirmationHtml(),
				allow_ticket_tracking: true,
				send_submitter_confirmation: true,
			});
			setEmailTemplateIndex(0);
			setActiveTab('general');
		}
	}, [currentState, itemType]);

	// select options
	const optionsDepartments = useSelector(state => state.departmentsReducer.departments?.map(dept => ({
		value: dept.id,
		label: dept.name,
		description: dept.description,
		color: dept.color
	})) || []);
	const optionsUsers = useSelector(state => state.usersReducer.users?.map(user => ({
		value: user.id,
		label: user.name,
		description: user.email,
		image: user.profile_image
	})) || []);
	const optionsRoles = [
		{ value: 'Admin', label: 'Admin' },
		{ value: 'Agent', label: 'Agent' },
		{ value: 'Customer', label: 'Customer' },
	];
	const ticketStatuses = useSelector(state => state.statusesReducer.statuses ?? []);

	// handle input changes
	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};
	const handleSelectChange = (name, value) => {
		setFormData({ ...formData, [name]: value });
	};
	const handleColorChange = (color) => {
		setFormData({ ...formData, color: color.hex });
	};
	const handleCloseColorPicker = (event) => {
		if (colorPickerRef && !colorPickerRef?.current?.contains(event.target)) {
			setColorPicker(false);
		}
	};

	const handleToggleNotifStatus = (statusId) => {
		const current = formData.notification_statuses;
		const updated = current.includes(statusId) ? current.filter(s => s !== statusId) : [...current, statusId];
		setFormData({ ...formData, notification_statuses: updated });
	};

	// ISO-8601: 1 = Monday ... 7 = Sunday — stored as numbers, locale-independent
	const DAYS = [
		{ value: 1, short: 'Mon' },
		{ value: 2, short: 'Tue' },
		{ value: 3, short: 'Wed' },
		{ value: 4, short: 'Thu' },
		{ value: 5, short: 'Fri' },
		{ value: 6, short: 'Sat' },
		{ value: 7, short: 'Sun' },
	];

	const handleToggleDay = (dayValue) => {
		const current = formData.notification_days;
		const updated = current.includes(dayValue)
			? current.filter(d => d !== dayValue)
			: [...current, dayValue].sort((a, b) => a - b);
		setFormData({ ...formData, notification_days: updated });
	};

	const cycleEmailConfirmationTemplate = () => {
		const nextIndex = (emailTemplateIndex + 1) % INCOMING_EMAIL_CONFIRMATION_TEMPLATES.length;
		const html = INCOMING_EMAIL_CONFIRMATION_TEMPLATES[nextIndex].html;
		setEmailTemplateIndex(nextIndex);
		setFormData((prev) => ({ ...prev, submitter_notification_body: html }));
		notificationEditorRef.current?.setHTML?.(html);
	};

	const handleAddNotifEmail = () => {
		const email = notifEmailInput.trim();
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setErrorMessage('Please enter a valid email address');
			return;
		}
		if (formData.notification_emails.some(e => e.value === email)) {
			setErrorMessage('This email address has already been added');
			return;
		}
		setFormData({ ...formData, notification_emails: [...formData.notification_emails, { value: email, label: email }] });
		setNotifEmailInput('');
		setErrorMessage('');
	};

	const handleRemoveNotifEmail = (email) => {
		setFormData({ ...formData, notification_emails: formData.notification_emails.filter(e => e.value !== email) });
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleCloseColorPicker);
		return () => {
			document.removeEventListener("mousedown", handleCloseColorPicker);
		};
	}, [colorPickerRef]);

	// handle actions
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (formData.name.length < 3) {
			setErrorMessage('Please fill in the name field with a minimum of 3 characters');
			return;
		}
		// prepare data
		let data = { name: formData.name };
		if (['tags', 'types', 'priorities', 'departments'].includes(itemType)) {
			data.color = formData.color;
		}
		if (['types', 'priorities', 'departments'].includes(itemType)) {
			data.description = formData.description;
		}
		if (['departments'].includes(itemType)) {
			data.members = formData.members.map(member => member.value);
			data.timezone = formData.timezone;
			data.notification_emails = formData.notification_emails.map(e => e.value);
			data.notification_statuses = formData.notification_statuses;
			data.notification_type = formData.notification_type;
			data.notification_days = formData.notification_days;
			data.notification_time = formData.notification_time;
			data.auto_assign_user_ids = formData.auto_assign_user_ids ?? [];
			if (formData.notification_type === 'schedule') {
				if (formData.notification_days.length === 0) {
					setActiveTab('notifications');
					setErrorMessage('Please select at least one day for the schedule.');
					return;
				}
				if (!formData.notification_time) {
					setActiveTab('notifications');
					setErrorMessage('Please select a time for the schedule.');
					return;
				}
				if (!formData.timezone) {
					setActiveTab('notifications');
					setErrorMessage('Please select a timezone for the schedule.');
					return;
				}
			}
		}
		if (['users'].includes(itemType)) {
			if (formData.email === '') {
				setErrorMessage('Please fill in the email field');
				return;
			}
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
				setErrorMessage('Please enter a valid email address');
				return;
			}
			data.email = formData.email;
			data.title = formData.title;
			data.role = formData.role;
			data.workspace_active = formData.workspace_active;
			data.departments = formData.departments.map(department => department.value);
		}
		if (['emails'].includes(itemType)) {
			if (formData.email_prefix.length < 3) {
				setErrorMessage('Please fill in the email field with a minimum of 3 characters');
				return;
			}
			if (formData.department === null) {
				setErrorMessage('Please select a department');
				return;
			}
			data.department_id = formData.department.value;
			data.email_prefix = formData.email_prefix;
			data.submitter_notification_body = notificationEditorRef.current?.getHTML?.() ?? formData.submitter_notification_body;
			data.allow_ticket_tracking = formData.allow_ticket_tracking;
			data.send_submitter_confirmation = formData.send_submitter_confirmation;
		}

		// handle request
		setIsLoading(true);
		try {
			const url = baseURL + (currentState?.title === 'Add' ? prefixAPI : `${prefixAPI}-update/${currentState.item.id}`);
			const response = await axios.post(url, data, headers);
			reduxRefresh();
			closeModal();
		} catch (error) {
			console.error(error);
			if (error.response.data.errors) {
				setErrorMessage(Object.values(error.response.data.errors)[0].message);
			} else {
				setErrorMessage(error.response.data.message || 'Something went wrong. Please try again later.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const renderGeneralFields = () => {
		const fields = [];
		fields.push(
			<Form.Group key="name" controlId="form.name">
				<Form.Label><b>Name</b></Form.Label>
				<Form.Control type="text" value={formData.name} name="name" onChange={handleInputChange} placeholder="Enter name" disabled={itemType == 'users' && currentState?.title === 'Edit'} required />
			</Form.Group>
		);

		if (['departments'].includes(itemType)) {
			fields.push(
				<Form.Group key="users" className='mt-3' controlId="form.users">
					<Form.Label><b>Users</b></Form.Label>
					<Select isMulti options={optionsUsers} components={{ Option: imageOption }} value={formData.members} onChange={(value) => handleSelectChange('members', value)} classNamePrefix="select-component" />
				</Form.Group>
			);
		}

		if (['users'].includes(itemType)) {
			fields.push(
				<Form.Group key="email" className='mt-3' sm={6} controlId="form.email">
					<Form.Label><b>Email</b></Form.Label>
					<Form.Control type="email" value={formData.email} name="email" onChange={handleInputChange} placeholder="Enter email" required disabled={currentState?.title === 'Edit'} />
				</Form.Group>,
				<Form.Group key="title" className='mt-3' sm={6} controlId="form.title">
					<Form.Label><b>Title</b></Form.Label>
					<Form.Control type="text" value={formData.title} name="title" onChange={handleInputChange} placeholder="e.g. Senior Developer" />
				</Form.Group>,
				<Form.Group key="departments" className='mt-3' controlId="form.departments">
					<Form.Label><b>Departments</b></Form.Label>
					<Select isMulti options={optionsDepartments} components={{ Option: colorOption, MultiValueLabel: colorValue }} value={formData.departments} onChange={(value) => handleSelectChange('departments', value)} classNamePrefix="select-component" />
				</Form.Group>,
				<Form.Group key="role" className='mt-3' controlId="form.role">
					<Form.Label><b>Role</b></Form.Label>
					{optionsRoles.map((option) => (
						<div key={option.value} className="mb-3">
							<Form.Check
								type="checkbox"
								id={`role-${option.value}`}
								label={option.label}
								checked={formData.role === option.value}
								onChange={(e) => handleSelectChange('role', e.target.checked ? option.value : '')}
							/>
							<Form.Text className="text-muted">
								{option.value === 'admin' ? 'Admins have full access to the system.' : 'Agents access to tickets and reports.'}
							</Form.Text>
						</div>
					))}
				</Form.Group>,
				<FormRule key="hr" />,
				<Form.Group key="workspace_active" className='mt-3' controlId="form.workspace_active">
					<Form.Label><b>System Access</b></Form.Label>
					<Form.Check
						type="checkbox"
						id="active"
						label='Active'
						checked={formData.workspace_active}
						onChange={(e) => handleSelectChange('workspace_active', e.target.checked)}
					/>
					<Form.Text className="text-muted">
						{formData.workspace_active ? 'User is active and can access the system.' : 'User is inactive and cannot access the system.'}
					</Form.Text>
				</Form.Group>
			);
		}

		if (['emails'].includes(itemType)) {
			fields.push(
				<Form.Group key="email_prefix" className='mt-3' controlId="form.email_prefix">
					<Form.Label><b>Email Address</b></Form.Label>
					<InputGroup>
						<Form.Control type="email" value={formData.email_prefix} name="email_prefix" onChange={handleInputChange} placeholder="Prefix (e.g. Acme-support)" required />
						<InputGroup.Text>{`@${process.env.MIX_EMAIL_DOMAIN}`}</InputGroup.Text>
					</InputGroup>
				</Form.Group>,
				<Form.Group key="department" className='mt-3' controlId="form.department">
					<Form.Label><b>Department</b></Form.Label>
					<Select options={optionsDepartments} components={{ Option: colorOption, SingleValue: colorValueSingle }} value={formData.department} onChange={(value) => handleSelectChange('department', value)} classNamePrefix="select-component" />
				</Form.Group>
			);
		}

		if (['types', 'priorities', 'departments'].includes(itemType)) {
			fields.push(
				<Form.Group key="description" className='mt-3' controlId="form.description">
					<Form.Label><b>Description</b></Form.Label>
					<Form.Control type="text" value={formData.description} name="description" onChange={handleInputChange} placeholder="Enter description" required />
				</Form.Group>
			);
		}

		if (['tags', 'types', 'priorities', 'departments'].includes(itemType)) {
			fields.push(
				<Form.Group key="color" className={styles.colorPicker} controlId="form.color">
					<Form.Label><b>Color</b></Form.Label>
					<div className="color-preview" style={{ backgroundColor: formData.color }}></div>
					<div className={styles.colorPickerWrapper}>
						<Button variant="light" className={styles.colorPickerButton} onClick={() => setColorPicker(!colorPicker)}><BsEyedropper /></Button>
						<CirclePicker color={formData.color} onChangeComplete={handleColorChange} />
					</div>
					{colorPicker && (
						<div className="color-picker-popover" ref={colorPickerRef}>
							<div className="color-picker-cover" onClick={handleCloseColorPicker} />
							<ChromePicker color={formData.color} onChange={handleColorChange} />
						</div>
					)}
				</Form.Group>
			);
		}

		return fields;
	};

	const renderNotificationsFields = () => (
		<>
			<Form.Group controlId="form.notification_emails">
				<Form.Label><b>Notification Emails</b></Form.Label>
				<Form.Text className="text-muted d-block mb-2">Email addresses to notify when a customer replies or a ticket status changes.</Form.Text>
				<InputGroup className="mb-2">
					<Form.Control
						type="email"
						value={notifEmailInput}
						onChange={(e) => setNotifEmailInput(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNotifEmail(); } }}
						placeholder="Enter email and press Add"
					/>
					<Button variant="secondary" onClick={handleAddNotifEmail}><BsPlus /> Add</Button>
				</InputGroup>
				{formData.notification_emails.length > 0 && (
					<>
						<Select
							isMulti
							menuIsOpen={false}
							components={{ DropdownIndicator: null, ClearIndicator: null }}
							value={formData.notification_emails}
							onChange={(selected) => setFormData({ ...formData, notification_emails: selected ?? [] })}
							classNamePrefix="select-component"
						/>
						<div className="mt-2">
							<Button variant="outline-danger" size="sm" style={{ transform: 'scale(0.9)', transformOrigin: 'left center' }} onClick={() => setFormData({ ...formData, notification_emails: [] })}>
								<BsTrash3 className="me-1" />Remove all
							</Button>
						</div>
					</>
				)}
			</Form.Group>
			<Form.Group className='mt-4' controlId="form.notification_statuses">
				<Form.Label><b>Notify on Status</b></Form.Label>
				<Form.Text className="text-muted d-block mb-2">Select which ticket statuses trigger a notification.</Form.Text>
				<div className="d-flex flex-wrap gap-3">
					{ticketStatuses.map(status => (
						<Form.Check
							key={status.id}
							type="checkbox"
							id={`notif-status-${status.id}`}
							label={
								<span className="d-inline-flex align-items-center gap-1">
									{status.color && (
										<span style={{ width: 10, height: 10, borderRadius: '50%', background: status.color, display: 'inline-block', flexShrink: 0 }} />
									)}
									{ucFirst(status.name)}
								</span>
							}
							checked={formData.notification_statuses.includes(status.id)}
							onChange={() => handleToggleNotifStatus(status.id)}
						/>
					))}
				</div>
				{ticketStatuses.length > 0 && (
					<div className="mt-2 d-flex gap-1">
						<Button variant="outline-secondary" size="sm" style={{ transform: 'scale(0.9)', transformOrigin: 'left center' }} onClick={() => setFormData({ ...formData, notification_statuses: ticketStatuses.map(s => s.id) })}>
							<BsCheckAll className="me-1" />Select all
						</Button>
						<Button variant="outline-secondary" size="sm" style={{ transform: 'scale(0.9)', transformOrigin: 'left center' }} onClick={() => setFormData({ ...formData, notification_statuses: [] })}>
							<BsDashCircle className="me-1" />Unselect all
						</Button>
					</div>
				)}
			</Form.Group>
			<Form.Group className='mt-4' controlId="form.timezone">
				<Form.Label><b>Timezone</b></Form.Label>
				<Form.Text className="text-muted d-block mb-2">Used to determine when scheduled notifications are sent.</Form.Text>
				<Select
					options={timezoneOptions}
					value={timezoneOptions.find(o => o.value === formData.timezone) ?? null}
					onChange={(option) => handleSelectChange('timezone', option ? option.value : '')}
					classNamePrefix="select-component"
					placeholder="Select timezone"
					isClearable
				/>
			</Form.Group>
			<Form.Group className='mt-4' controlId="form.notification_schedule">
				<Form.Label><b>Delivery Schedule</b></Form.Label>
				<Form.Text className="text-muted d-block mb-2">Choose when notifications should be sent.</Form.Text>
				<div className="btn-group mb-3" role="group">
					<Button
						type="button"
						size="sm"
						variant={formData.notification_type === 'immediately' ? 'primary' : 'outline-secondary'}
						onClick={() => setFormData({ ...formData, notification_type: 'immediately' })}
					>
						Send Immediately
					</Button>
					<Button
						type="button"
						size="sm"
						variant={formData.notification_type === 'schedule' ? 'primary' : 'outline-secondary'}
						onClick={() => setFormData({ ...formData, notification_type: 'schedule' })}
					>
						Schedule
					</Button>
				</div>
				{formData.notification_type === 'schedule' && (
					<>
						<div className="d-flex flex-wrap gap-2 mb-3">
							{DAYS.map(day => (
								<span
									key={day.value}
									onClick={() => handleToggleDay(day.value)}
									style={{
										cursor: 'pointer',
										padding: '3px 10px',
										borderRadius: '20px',
										border: '1px solid',
										fontSize: '0.8rem',
										userSelect: 'none',
										...(formData.notification_days.includes(day.value)
											? { background: '#0d6efd', color: '#fff', borderColor: '#0d6efd' }
											: { background: '#fff', color: '#6c757d', borderColor: '#dee2e6' })
									}}
								>
									{day.short}
								</span>
							))}
						</div>
						<Form.Control
							type="time"
							value={formData.notification_time}
							onChange={(e) => setFormData({ ...formData, notification_time: e.target.value })}
							style={{ maxWidth: '140px' }}
						/>
					</>
				)}
			</Form.Group>
		</>
	);

	const autoAssignSelectedIds = formData.auto_assign_user_ids ?? [];
	const autoAssignValue = optionsUsers.filter(user => autoAssignSelectedIds.includes(Number(user.value)));

	const renderAutoAssignFields = () => (
		<Form.Group controlId="form.auto_assign_users">
			<Form.Label><b>Auto-assign users</b></Form.Label>
			<Form.Text className="text-muted d-block mb-2">
				When an email arrives at this department&apos;s inbox, or a new ticket is created for it, these users are assigned immediately.
			</Form.Text>
			<Select
				isMulti
				isClearable
				closeMenuOnSelect={false}
				options={optionsUsers}
				components={{ Option: imageOption }}
				value={autoAssignValue}
				onChange={(value) => handleSelectChange('auto_assign_user_ids', (value ?? []).map(user => Number(user.value)))}
				classNamePrefix="select-component"
				placeholder="Search users…"
			/>
		</Form.Group>
	);

	const renderEmailNotificationsFields = () => {
		const activeTemplate = INCOMING_EMAIL_CONFIRMATION_TEMPLATES[emailTemplateIndex];

		return (
		<>
			<Form.Group controlId="form.send_submitter_confirmation">
				<Form.Check
					type="switch"
					id="send-submitter-confirmation"
					label="Send &quot;We&apos;ve received your message&quot; confirmation email"
					checked={formData.send_submitter_confirmation}
					onChange={(e) => setFormData({ ...formData, send_submitter_confirmation: e.target.checked })}
				/>
				<Form.Text className="text-muted">
					When enabled, the person who emails this inbox gets an automatic confirmation when a new ticket is created.
					Agent replies from Claritick are always sent regardless of this setting.
				</Form.Text>
			</Form.Group>
			<Form.Group className="mt-4" controlId="form.submitter_notification_body">
				<Form.Label><b>Confirmation email message</b></Form.Label>
				<Form.Text className="text-muted d-block mb-2 small">
					Auto-reply on new ticket · placeholders: <code>{'{{name}}'}</code>, <code>{'{{ticket_id}}'}</code>
					{formData.allow_ticket_tracking && (
						<>, <code>{'{{url}}'}</code></>
					)}
					{' · text only (no links, images, or files in the editor)'}
				</Form.Text>
				<div className="d-flex flex-wrap align-items-center gap-2 mb-2">
					<Button
						type="button"
						variant="outline-secondary"
						size="sm"
						onClick={cycleEmailConfirmationTemplate}
						disabled={!formData.send_submitter_confirmation}
					>
						Swap template ({emailTemplateIndex + 1}/{INCOMING_EMAIL_CONFIRMATION_TEMPLATES.length})
					</Button>
					<span className="text-muted small">{activeTemplate.label}</span>
				</div>
				<div style={formData.send_submitter_confirmation ? undefined : { opacity: 0.55, pointerEvents: 'none' }}>
					<CustomEditor
						ref={notificationEditorRef}
						comfortable
						defaultValue={formData.submitter_notification_body || getDefaultIncomingEmailConfirmationHtml()}
						placeholder="Write the confirmation message..."
					/>
				</div>
			</Form.Group>
			<Form.Group className="mt-4" controlId="form.allow_ticket_tracking">
				<Form.Check
					type="switch"
					id="allow-ticket-tracking"
					label="Allow the person to track the opening ticket progress"
					checked={formData.allow_ticket_tracking}
					onChange={(e) => setFormData({ ...formData, allow_ticket_tracking: e.target.checked })}
					disabled={!formData.send_submitter_confirmation}
				/>
				<Form.Text className="text-muted">
					When enabled, the confirmation email includes a button and link to view ticket progress.
				</Form.Text>
			</Form.Group>
		</>
		);
	};

	const usesTabbedEditModal =
		itemType === 'emails' ||
		(itemType === 'departments' && currentState?.title === 'Edit');

	return (
		<Form onSubmit={handleSubmit}>
			<Modal show={true} onHide={closeModal}>
				<Modal.Header closeButton>
					<Modal.Title>{currentState?.title === 'Edit' ? 'Edit' : 'New'} {single}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
					{usesTabbedEditModal ? (
						<>
							<Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={setActiveTab}>
								<Nav.Item>
									<Nav.Link eventKey="general" style={{ color: 'black' }}>
										{itemType === 'emails' ? 'General Info' : 'General'}
									</Nav.Link>
								</Nav.Item>
								<Nav.Item>
									<Nav.Link eventKey="notifications" style={{ color: 'black' }}>Notifications</Nav.Link>
								</Nav.Item>
								{itemType === 'departments' && (
									<Nav.Item>
										<Nav.Link eventKey="auto-assign" style={{ color: 'black' }}>Auto Assign</Nav.Link>
									</Nav.Item>
								)}
							</Nav>
							{activeTab === 'general' && renderGeneralFields()}
							{activeTab === 'notifications' && (
								itemType === 'emails' ? renderEmailNotificationsFields() : renderNotificationsFields()
							)}
							{itemType === 'departments' && activeTab === 'auto-assign' && renderAutoAssignFields()}
						</>
					) : (
						renderGeneralFields()
					)}
				</Modal.Body>
				<Modal.Footer>
					{currentState?.title === 'Edit' && (
						<Button variant="danger" className='mr-auto' onClick={() => showDeleteConfirm(currentState.item, single, prefixAPI, reduxRefresh, closeModal)} disabled={isLoading}><BsTrash3 /> Delete</Button>
					)}
					<Button variant="secondary" onClick={closeModal} disabled={isLoading}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={isLoading} onClick={handleSubmit}>
						{isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save'}
					</Button>
				</Modal.Footer>
			</Modal>
		</Form>
	);
}

export default ItemModal;
